import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';

const FAQPage = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const faqs = [
    {
      question: "How long does the NOC approval process take?",
      answer: "The complete approval process takes 30 working days from submission. Your application goes through 4 stages: L1 Review, L2 Review, and Collector Approval."
    },
    {
      question: "Can I apply for multiple tanks in different districts?",
      answer: "Yes, you can select and apply for multiple tanks across different districts in a single application. However, each tank may be processed by its respective district department, which could affect processing timelines."
    },
    {
      question: "What happens if my application is rejected at any stage?",
      answer: "If rejected, you will receive specific reasons via email and SMS. You can resubmit your application after addressing the concerns raised by the reviewing department. There is no limit on resubmissions."
    },
    {
      question: "Is there any fee for submitting NOC applications?",
      answer: "No, the NOC application process is completely free of charge. There are no application fees or processing charges for submitting your water tank conservation project proposals through this portal."
    }
  ];

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-slate-100">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        .faq-font {
          font-family: 'Inter', system-ui, sans-serif;
        }
        
        body, html {
          margin: 0;
          padding: 0;
          min-height: 100vh;
        }
      `}</style>
      
      <section className="py-36 faq-font">
        <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
          
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-semibold text-slate-800 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Find answers to common questions about the application process and requirements
            </p>
          </div>

          {/* FAQ Items */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm divide-y divide-gray-200">
            {faqs.map((faq, index) => (
              <div key={index} className="transition-colors duration-200">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-5 text-left focus:outline-none focus:bg-gray-50 hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-slate-800 pr-8">
                      {faq.question}
                    </h3>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      {openItems.has(index) ? 
                        <Minus className="w-5 h-5 text-slate-600" /> : 
                        <Plus className="w-5 h-5 text-slate-600" />
                      }
                    </div>
                  </div>
                </button>
                
                <div className={`transition-all duration-300 ease-in-out ${
                  openItems.has(index) 
                    ? 'max-h-40 opacity-100' 
                    : 'max-h-0 opacity-0'
                } overflow-hidden`}>
                  <div className="px-6 pb-5">
                    <p className="text-slate-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Contact Section */}
          <div className="mt-12 text-center">
            <div className="bg-slate-100 border border-gray-300 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-slate-800 mb-3">
                Need Additional Help?
              </h3>
              <p className="text-slate-600 mb-6 max-w-xl mx-auto">
                If you have questions that aren't covered here, please contact our support team for assistance.
              </p>
              <button className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200">
                Contact Support
              </button>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default FAQPage;